import {
  EnumEntityReferenceType,
  EnumUserAccessOperatorsRole,
  EnumUserAccessOperatorsStatus,
  OperatorStaffQuery,
  UserAccessOperators,
} from "gql/generated";
import { staffCard } from "./staffCard.recipe";
import { getNameFromUser } from "shared/utils/getNameFromUser";

// The status is derived from a combination of "role" and "status"
// status === INVITED superceeds the role
export enum TagStatus {
  Requested = "REQUESTED",
  NotInvited = "NOT_INVITED",
  Invited = "INVITED",
  Staff = "STAFF",
  Owner = "OWNER",
}

// TODO : Add an action to transfer ownership when Olivier completes that flow
// TODO : That said, there is currently NO access difference between OWNER and STAFF
export enum TagAction {
  Accept = "ACCEPT",
  Delete = "DELETE",
  Invite = "INVITE",
  Remove = "REMOVE",
  Resend = "RESEND",
}

type UserStaffCardProps = {
  _entityId: string;
  type: EnumEntityReferenceType;
  staffMember: OperatorStaffQuery["operatorStaff"][0];
  onAcceptClick: () => void;
  onRemoveClick: (action: TagAction) => void;
  onResendInviteClick: () => void;
  onSendInviteClick: () => void;
};

export const useStaffCard = ({
  _entityId,
  type,
  staffMember,
  onAcceptClick,
  onRemoveClick,
  onResendInviteClick,
  onSendInviteClick,
}: UserStaffCardProps) => {
  // Get the access document for the matching entity
  const entityAccess: UserAccessOperators | null | undefined =
    type === EnumEntityReferenceType.Operator
      ? staffMember.access.operators?.find(
          (item) => item?.entityId === _entityId,
        )
      : staffMember.access.organizations?.find(
          (item) => item?.entityId === _entityId,
        );
  const userRole = entityAccess?.role;
  const inviteStatus = entityAccess?.status;

  // Determine the visible tag status
  const tagStatus =
    inviteStatus === EnumUserAccessOperatorsStatus.Invited
      ? TagStatus.Invited
      : inviteStatus === EnumUserAccessOperatorsStatus.NotInvited
        ? TagStatus.NotInvited
        : inviteStatus === EnumUserAccessOperatorsStatus.Requested
          ? TagStatus.Requested
          : userRole === EnumUserAccessOperatorsRole.Staff
            ? TagStatus.Staff
            : TagStatus.Owner;

  const fullName = getNameFromUser(staffMember);

  const visual = tagStatus === TagStatus.Invited ? "invited" : "accepted";
  const classes = staffCard({ visual });

  /**
   * Handle clicking on an action button from the popup menu
   * @param item
   */
  const handleStaffAction = (item: { value: string }) => {
    const action = item.value as TagAction;
    switch (action) {
      case TagAction.Accept:
        onAcceptClick();
        break;
      case TagAction.Delete:
      case TagAction.Remove:
        onRemoveClick(action);
        break;
      case TagAction.Invite:
        onSendInviteClick();
        break;
      case TagAction.Resend:
        onResendInviteClick();
        break;
    }
  };

  return {
    classes,
    fullName,
    tagStatus,
    handleStaffAction,
  };
};
